const formatConfirmAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return String(value);
  }
  return `₹ ${num.toLocaleString('en-IN')}`;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const isNonEmptyObject = (value) =>
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0;

/** Build confirm-modal summary from viewContract postsale_data. */
export const buildInvoiceConfirmDetails = (postsale) => {
  if (!postsale || typeof postsale !== 'object') {
    return { summaryRows: [], sections: [] };
  }

  const commercial = postsale.commercial || {};
  const contractDetails = postsale.contract_details || {};
  const milestone = postsale.milestone || {};
  const capex = postsale.capex_invoicing_details;
  const invoiceModule = asArray(postsale.invoice_module);
  const manualInvoice = commercial.manual_invoice || {};

  const leadTags = asArray(postsale.lead_tags);
  const summaryRows = [
    {
      label: 'Commercial type',
      value: commercial.commercialName || commercial.commercial_name || null,
    },
    {
      label: 'Solution',
      value: commercial.solution_type_name || null,
    },
    {
      label: 'Revenue nature',
      value: commercial.revenue_nature || postsale.revenue_nature || null,
    },
    {
      label: 'Invoice type',
      value: commercial.invoice_type || postsale.invoice_type || null,
    },
    {
      label: 'Payment term',
      value: commercial.payment_term || null,
    },
    {
      label: 'Payment due days',
      value: commercial.payment_due_days_name || commercial.payment_due_days || null,
    },
    {
      label: 'Type of sales',
      value: commercial.type_of_sales || postsale.type_of_sales || null,
    },
    {
      label: 'Contract length',
      value: commercial.contract_length
        ? `${commercial.contract_length} month(s)`
        : contractDetails.contract_period || commercial.contract_period || null,
    },
    {
      label: 'Contract dates',
      value:
        contractDetails.contract_from_date || commercial.contract_from_date
          ? `${
              contractDetails.contract_from_date ||
              commercial.contract_from_date ||
              '—'
            } → ${
              contractDetails.contract_to_date ||
              commercial.contract_to_date ||
              '—'
            }`
          : null,
    },
    {
      label: 'Subscription amount',
      value: formatConfirmAmount(commercial.subscription_amount),
    },
    {
      label: 'Installation amount',
      value: formatConfirmAmount(commercial.installation_amount),
    },
    {
      label: 'Tags',
      value: leadTags.length
        ? `${leadTags.length} tag(s) — ${leadTags
            .slice(0, 3)
            .map((tag) => tag?.name)
            .filter(Boolean)
            .join(', ')}`
        : null,
    },
  ].filter((row) => row.value);

  const sections = [];
  const milestoneData = asArray(milestone.milestone_data);
  const datedInvoices = asArray(milestone.date_invoices_data);
  const retention = asArray(milestone.retention);
  const hasCapex = isNonEmptyObject(capex);

  if (
    !hasCapex &&
    (milestoneData.length || datedInvoices.length || retention.length)
  ) {
    const lines = [];
    if (milestoneData.length) {
      lines.push(`${milestoneData.length} milestone invoice(s)`);
      milestoneData.slice(0, 8).forEach((item, index) => {
        const amount = formatConfirmAmount(item?.amt);
        lines.push(
          `Milestone ${index + 1}: ${item?.stage_name || '—'} — ${amount || '—'}${
            item?.desc ? ` (${item.desc})` : ''
          }`
        );
      });
    }
    if (datedInvoices.length) {
      lines.push(`${datedInvoices.length} dated invoice(s)`);
      datedInvoices.slice(0, 8).forEach((item, index) => {
        const amount = formatConfirmAmount(item?.amt);
        lines.push(
          `Dated ${index + 1}: ${amount || '—'}${
            item?.inv_date ? ` on ${item.inv_date}` : ''
          }`
        );
      });
    }
    if (retention.length) {
      lines.push(`${retention.length} retention invoice(s)`);
      retention.slice(0, 5).forEach((item, index) => {
        const amount = formatConfirmAmount(item?.amt);
        lines.push(`Retention ${index + 1}: ${amount || '—'}`);
      });
    }
    sections.push({ title: 'Milestone details', lines });
  }

  if (hasCapex) {
    const invoiceCount =
      capex.invoice_count != null && capex.invoice_count !== ''
        ? capex.invoice_count
        : null;
    const lines = [
      invoiceCount != null ? `No. of invoices: ${invoiceCount}` : null,
      capex.invoice_frequency_value
        ? `Frequency: ${capex.invoice_frequency_value}`
        : null,
      formatConfirmAmount(capex.total_amount)
        ? `Total amount: ${formatConfirmAmount(capex.total_amount)}`
        : null,
      capex.invoice_nomenclature_value
        ? `Hardware frequency: ${capex.invoice_nomenclature_value}`
        : null,
    ].filter(Boolean);
    if (lines.length) {
      sections.push({ title: 'Capex invoicing', lines });
    }
  }

  const revenueNature = String(
    commercial.revenue_nature || postsale.revenue_nature || ''
  ).toLowerCase();
  const invoiceType = String(
    commercial.invoice_type || postsale.invoice_type || ''
  ).toLowerCase();
  const looksRental =
    revenueNature.includes('rental') ||
    invoiceType.includes('rental') ||
    String(manualInvoice.rental_check || '').toLowerCase() === 'yes';

  if (invoiceModule.length && !hasCapex) {
    const lines = [
      `No. of invoice line items: ${invoiceModule.length}`,
      ...invoiceModule.slice(0, 8).map((item) => {
        const amount = formatConfirmAmount(
          item?.subscription_amount ?? item?.sub_amount
        );
        return `${item?.name || item?.product || 'Line item'}: ${amount || '—'}`;
      }),
    ];
    sections.push({
      title: looksRental ? 'Rental invoices' : 'Invoice module',
      lines,
    });
  } else if (looksRental) {
    const lines = [
      manualInvoice.rental_check
        ? `Rental/Capex check: ${manualInvoice.rental_check}`
        : null,
      manualInvoice.rental_invoice_number
        ? `1st invoice number: ${manualInvoice.rental_invoice_number}`
        : null,
      formatConfirmAmount(commercial.subscription_amount)
        ? `Subscription amount: ${formatConfirmAmount(
            commercial.subscription_amount
          )}`
        : null,
      commercial.pre_invoice_day != null && commercial.pre_invoice_day !== ''
        ? `Pre-invoice day: ${commercial.pre_invoice_day}`
        : null,
    ].filter(Boolean);
    if (lines.length) {
      sections.push({ title: 'Rental details', lines });
    }
  }

  if (
    manualInvoice.rental_invoice_number &&
    !sections.some((section) =>
      section.lines.some((line) => String(line).includes('1st invoice number'))
    )
  ) {
    sections.push({
      title: 'Manual invoice',
      lines: [`1st invoice number: ${manualInvoice.rental_invoice_number}`],
    });
  }

  return { summaryRows, sections };
};
